import { useQuery } from "@tanstack/react-query";
import { useDispatch, useSelector } from "react-redux";
import { useEffect } from "react";

import { userApi } from "@/api/user";
import { setIsNewUser } from "@/store/telegramSlice";
import { logMsg, logEx, crumb, setUserContext } from "@/config/sentry-logger";

import type { AxiosError, AxiosResponse } from "axios";
import type { AppDispatch, RootState } from "@/store";
import type { TUser } from "@/modules/shared/types/user";

const USER_QUERY_KEY = "user-query-key";

export const useUser = () => {
  const initData = useSelector((state: RootState) => state.telegram.initData);
  const dispatch = useDispatch<AppDispatch>();

  const enabled = !!initData;

  const {
    data: user,
    isLoading: isLoadingUser,
    isSuccess,
    isError,
    error,
    ...queryParams
  } = useQuery<AxiosResponse<TUser>, Error, TUser>({
    queryKey: [USER_QUERY_KEY, initData],
    queryFn: () => userApi.updateUser(initData!, {}),
    select: (res) => res.data,
    enabled,
  });

  useEffect(() => {
    if (!enabled) {
      logMsg("useUser disabled", "debug", { hasInitData: !!initData });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  useEffect(() => {
    if (!isSuccess || !user) return;

    crumb("RQ useUser success", {
      hasInitData: !!initData,
      userId: user.id,
    });
    logMsg("useUser success", "info", { userId: user.id });

    setUserContext({
      id: user.id,
      username: (user as any)?.username,
      tonAddress: (user as any)?.tonAddress,
      locale: (user as any)?.locale,
      country: (user as any)?.country,
    });
  }, [isSuccess, user, initData]);

  useEffect(() => {
    if (!isError || !error) return;

    const ax = error as AxiosError;
    const status = ax.response?.status;
    const extra = {
      status,
      code: (ax as any)?.code,
      method: ax.config?.method,
      url: ax.config?.url,
      hasInitData: !!initData,
    };
    const level = !status ? "error" : status >= 500 ? "error" : "warning";

    logMsg("useUser error", level, extra);
    logEx(error, "useUser onError", extra);
  }, [isError, error, initData]);

  useEffect(() => {
    if (user && !user.onboardingWasShown) {
      crumb("user flagged as new", { userId: user.id });
      dispatch(setIsNewUser(true));
    }
  }, [user?.onboardingWasShown, user?.id, dispatch]);

  return {
    user,
    isLoadingUser,
    ...queryParams,
  };
};

export { USER_QUERY_KEY };
